/**
 * テンプレート編集モーダル
 *
 * CSVテンプレートの作成・編集用モーダルコンポーネント
 */

import { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Group,
  TextInput,
  Textarea,
  Text,
  Badge,
  Button,
} from "@mantine/core";
import { Trash2 } from "lucide-react";
import { CsvTemplateCommon, CsvColumn, CsvEncoding } from "../../services/csvSettingsService";
import { useColumnOperations } from "../../hooks/useColumnOperations";
import { ColumnDefinitionEditor } from "./ColumnDefinitionEditor";
import { EncodingSelect } from "./EncodingSelect";

interface TemplateEditModalProps {
  opened: boolean;
  onClose: () => void;
  template: CsvTemplateCommon | null;
  onSave: (data: TemplateFormData) => Promise<void>;
  onDelete?: (template: CsvTemplateCommon) => void;
  saving: boolean;
}

export interface TemplateFormData {
  name: string;
  description: string;
  encoding: CsvEncoding;
  columns: CsvColumn[];
  promptAddition: string;
}

/**
 * テンプレート編集モーダル
 */
export function TemplateEditModal({
  opened,
  onClose,
  template,
  onSave,
  onDelete,
  saving,
}: TemplateEditModalProps) {
  // 編集中のデータ
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [encoding, setEncoding] = useState<CsvEncoding>("utf-8");
  const [promptAddition, setPromptAddition] = useState("");

  // カラム操作フック
  const {
    columns,
    addColumn,
    updateColumn,
    removeColumn,
    moveColumn,
    resetColumns,
  } = useColumnOperations();

  // テンプレートが変更されたらフォームをリセット
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description);
      setEncoding(template.encoding);
      resetColumns(template.columns);
      setPromptAddition(template.promptAddition || "");
    } else {
      // 新規作成モード
      setName("");
      setDescription("");
      setEncoding("utf-8");
      resetColumns([
        { order: 1, headerName: "氏名", jsonPath: "氏名", required: true },
        { order: 2, headerName: "金額", jsonPath: "金額", required: false },
      ]);
      setPromptAddition("");
    }
  }, [template, opened, resetColumns]);

  const handleSave = async () => {
    await onSave({
      name,
      description,
      encoding,
      columns: columns.map((c, i) => ({ ...c, order: i + 1 })),
      promptAddition,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={template ? "テンプレート編集" : "テンプレート作成"}
      size="xl"
    >
      <Stack gap="md">
        <Group grow>
          <TextInput
            label="テンプレート名"
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <EncodingSelect value={encoding} onChange={setEncoding} />
        </Group>

        <Textarea
          label="説明"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />

        {/* 列定義エディタ */}
        <ColumnDefinitionEditor
          columns={columns}
          onAddColumn={addColumn}
          onUpdateColumn={updateColumn}
          onRemoveColumn={removeColumn}
          onMoveColumn={moveColumn}
        />

        <Textarea
          label={
            <Group gap="xs">
              <Text component="span" size="sm" fw={500}>
                追加プロンプト
              </Text>
              <Badge size="xs" variant="light" color="gray">
                任意
              </Badge>
            </Group>
          }
          description="基本的には入力不要です。読み取り結果が思わしくない場合や、特定の出力形式（例：「日付はYYYY/MM/DD」など）を指定したい場合にのみ利用してください。"
          placeholder="例: 日付はYYYY/MM/DD形式で出力してください"
          minRows={3}
          value={promptAddition}
          onChange={(e) => setPromptAddition(e.currentTarget.value)}
        />

        <Group justify="space-between" mt="md">
          {template && onDelete ? (
            <Button
              color="red"
              variant="subtle"
              leftSection={<Trash2 size={16} />}
              onClick={() => {
                onClose();
                onDelete(template);
              }}
            >
              このテンプレートを削除
            </Button>
          ) : (
            <div />
          )}
          <Group gap="sm">
            <Button variant="default" onClick={onClose}>
              キャンセル
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={!name}>
              保存
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
